import React, { useEffect, useMemo, useState } from "react";
import { formatoDecimal } from "../../../utilities/formatoNumeros";
import TopSeller from "./TopSeller";
import TopTipoPublicaciones from "./TopTipoPublicaciones";
import { normalizeTarjetasData } from "./DashboardHelper";
import { useIsMobile } from "../../../hooks/useIsMobile";

const tablaEstilo = {
  fondo: "#fffafb",
  header: "#ffe5e7",
  borde: "1px solid #ffd0d5",
};

const BadgeVar = ({ label, value, suffix = "%" }) => {
  const n = Number(value ?? 0);
  const isNeg = n < 0;
  return (
    <span
      className={`dashboard-badge-var mx-1 ${isNeg ? "dashboard-badge-var--down" : "dashboard-badge-var--up"}`}
      title={`${label}: ${n.toFixed(2)}${suffix}`}
    >
      <i className={`mdi ${isNeg ? "mdi-arrow-down-bold" : "mdi-arrow-up-bold"} mr-1`} aria-hidden="true" />
      {label}: {n.toFixed(2)}
      {suffix}
    </span>
  );
};

const KpiIcon = ({ icon, variant }) => (
  <div className={`dashboard-atributos__icon dashboard-atributos__icon--${variant} mx-auto mb-2`}>
    <i className={icon} aria-hidden="true" />
  </div>
);

export default function TarjetasDashboard({
  data,
  onSelectProduct,
  tabProductos,
  setTabProductos,
  topSellers,
  tipoPublicaciones,
  userType,
  currentUserType,
}) {
  const d = normalizeTarjetasData(data);
  const isMobile = useIsMobile();
  const isFullAdmin = currentUserType == 1 || currentUserType == 2;
  const isAuthorizedSeller = !!(userType?.isSeller || userType?.isSellerAdmin);
  const [isCarouselPaused, setIsCarouselPaused] = useState(false);

  const slides = useMemo(() => {
    const availableSlides = [
      {
        id: "top-productos",
        title: "Top Productos",
        icon: "mdi mdi-trophy-variant-outline",
        subtitle: "Productos con mejor performance de ventas",
      },
    ];

    if (isFullAdmin) {
      availableSlides.push({
        id: "top-sellers",
        title: "Top Sellers",
        icon: "mdi mdi-account-multiple",
        subtitle: "Sellers con mayor volumen de unidades",
      });
    }

    if (isFullAdmin || isAuthorizedSeller) {
      availableSlides.push({
        id: "top-tipos",
        title: "Top Tipo Publicaciones",
        icon: "mdi mdi-format-list-bulleted-type",
        subtitle: "Tipos de publicación con mejor rendimiento",
      });
    }

    return availableSlides;
  }, [isAuthorizedSeller, isFullAdmin]);

  useEffect(() => {
    if (tabProductos >= slides.length) {
      setTabProductos(0);
    }
  }, [slides.length, tabProductos, setTabProductos]);

  useEffect(() => {
    if (isMobile || slides.length <= 1 || isCarouselPaused) {
      return undefined;
    }

    const timer = setInterval(() => {
      setTabProductos((tabProductos + 1) % slides.length);
    }, 3000);

    return () => clearInterval(timer);
  }, [isCarouselPaused, isMobile, setTabProductos, slides.length, tabProductos]);

  const goToSlide = (targetIndex) => {
    if (slides.length <= 0) return;
    const safeIndex = (targetIndex + slides.length) % slides.length;
    setTabProductos(safeIndex);
  };

  const renderTopContent = (slideId) => {
    if (slideId === "top-productos") {
      return (
        <div className="table-responsive mb-1">
          <table className="table table-sm table-hover mb-0">
            <thead className="thead-light">
              <tr>
                <th>Código</th>
                <th>Producto</th>
                <th className="text-right">Unidades</th>
                <th className="text-right">Ventas</th>
              </tr>
            </thead>
            <tbody>
              {(d.topProductos || []).length > 0 ? (
                d.topProductos.map((p, idx) => (
                  <tr
                    key={`${p.codigoProducto}-${idx}`}
                    role="button"
                    style={{ cursor: "pointer" }}
                    onClick={() => onSelectProduct && onSelectProduct(p)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        onSelectProduct && onSelectProduct(p);
                      }
                    }}
                    tabIndex={0}
                    title="Buscar publicaciones por este código"
                  >
                    <td>{p.codigoProducto}</td>
                    <td>{p.nombreProducto}</td>
                    <td className="text-right">{p.unidadesVendidas}</td>
                    <td className="text-right">${formatoDecimal(`${p.montoVentas}`)}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={4} className="text-center text-muted py-3">
                    Sin datos
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      );
    }

    if (slideId === "top-sellers") {
      return <TopSeller sellers={topSellers} tablaEstilo={tablaEstilo} />;
    }

    if (slideId === "top-tipos") {
      return <TopTipoPublicaciones tipos={tipoPublicaciones} tablaEstilo={tablaEstilo} />;
    }

    return null;
  };

  return (
    <>
      <div className="row position-relative mb-4 dashboard-kpi-row">
        <div className="col-12 col-lg-3 mb-3">
          <div className="p-3 rounded shadow-sm h-100 dashboard-atributos kpi-destacado text-center">
            <KpiIcon icon="mdi mdi-cash-multiple" variant="ventas" />
            <h6 className="text-muted mb-1">Total Ventas</h6>
            <h2 className="m-0">${formatoDecimal(`${d.ventasDelMes}`)}</h2>
            <div className="mt-2">
              <BadgeVar label={isMobile ? "Mes" : "Var. mensual ventas"} value={d.variacionMensualVentas} />
              <BadgeVar label={isMobile ? "Año" : "Var. anual ventas"} value={d.variacionAnualVentas} />
            </div>
          </div>
        </div>

        <div className="col-12 col-lg-3 mb-3">
          <div className="p-3 rounded shadow-sm h-100 dashboard-atributos text-center">
            <KpiIcon icon="mdi mdi-percent" variant="comision" />
            <h6 className="text-muted mb-1">Comisión Ventas</h6>
            <h3 className="m-0">${formatoDecimal(`${d.comisionVentas}`)}</h3>
          </div>
        </div>

        <div className="col-12 col-lg-3 mb-3">
          <div className="p-3 rounded shadow-sm h-100 dashboard-atributos text-center">
            <KpiIcon icon="mdi mdi-receipt" variant="ticket" />
            <h6 className="text-muted mb-1">Ticket Promedio</h6>
            <h3 className="m-0">${formatoDecimal(`${d.ticketPromedio}`)}</h3>
          </div>
        </div>

        <div className="col-12 col-lg-3 mb-3">
          <div className="p-3 rounded shadow-sm h-100 dashboard-atributos text-center">
            <KpiIcon icon="mdi mdi-package-variant-closed" variant="unidades" />
            <h6 className="text-muted mb-1">Unidades Vendidas</h6>
            <h3 className="m-0">{d.unidadesVendidas}</h3>
            <div className="mt-2">
              <BadgeVar label={isMobile ? "Mes" : "Var. mensual unidades"} value={d.variacionMensualUnidades} />
              <BadgeVar label={isMobile ? "Año" : "Var. anual unidades"} value={d.variacionAnualUnidades} />
            </div>
          </div>
        </div>
      </div>

      {isMobile ? (
        <div className="dashboard-tops">
          {slides.map((slide) => (
            <section key={slide.id} className="dashboard-top-block p-3 rounded shadow-sm dashboard-container">
              <div className="dashboard-top-carousel__header mb-2">
                <div className="d-flex align-items-center">
                  <div className="dashboard-top-carousel__icon mr-2">
                    <i className={slide.icon} />
                  </div>
                  <div>
                    <h5 className="m-0">{slide.title}</h5>
                    <small className="text-muted">{slide.subtitle}</small>
                  </div>
                </div>
              </div>
              {renderTopContent(slide.id)}
            </section>
          ))}
        </div>
      ) : (
      <div className="col-12">
        <div className="p-3 rounded shadow-sm dashboard-container">
          <div
            className="dashboard-top-carousel"
            onMouseEnter={() => setIsCarouselPaused(true)}
            onMouseLeave={() => setIsCarouselPaused(false)}
          >
            <div className="dashboard-top-carousel__header mb-3">
              <div className="d-flex align-items-center">
                <div className="dashboard-top-carousel__icon mr-2">
                  <i className={slides[tabProductos]?.icon || "mdi mdi-trophy-variant-outline"} />
                </div>
                <div>
                  <h5 className="m-0">{slides[tabProductos]?.title || "Top Productos"}</h5>
                  <small className="text-muted">
                    {slides[tabProductos]?.subtitle || "Resumen de topes del período seleccionado"}
                  </small>
                </div>
              </div>
            </div>

            <div className="dashboard-top-carousel__body">
              {/* desktop carousel */}
              {slides.length > 1 && (
                <button
                  type="button"
                  className="dashboard-top-carousel__arrow dashboard-top-carousel__arrow--left"
                  onClick={() => goToSlide(tabProductos - 1)}
                  aria-label="Ir al bloque anterior"
                >
                  <i className="mdi mdi-chevron-left" />
                </button>
              )}

              <div className="dashboard-top-carousel__viewport">
                <section className="dashboard-top-carousel__slide">
                  {renderTopContent(slides[tabProductos]?.id)}
                </section>
              </div>

              {slides.length > 1 && (
                <button
                  type="button"
                  className="dashboard-top-carousel__arrow dashboard-top-carousel__arrow--right"
                  onClick={() => goToSlide(tabProductos + 1)}
                  aria-label="Ir al bloque siguiente"
                >
                  <i className="mdi mdi-chevron-right" />
                </button>
              )}
            </div>

            {slides.length > 1 && (
              <div className="dashboard-top-carousel__dots mt-3">
                {slides.map((slide, index) => (
                  <button
                    key={slide.id}
                    type="button"
                    className={`dashboard-top-carousel__dot ${tabProductos === index ? "is-active" : ""}`}
                    onClick={() => goToSlide(index)}
                    aria-label={`Ir a ${slide.title}`}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
      )}
    </>
  );
}
